import { useState } from 'react';
import { useIsMobileScreenWidthOrThinner } from '../../../helpers/hooks/useScreenWidth';
import { InternalLink } from '../..//Clickables/InternalLink/InternalLink';
import { Heading } from '../..//Heading/Heading';
import { Button } from '../../Clickables/Buttons/Button';
import { ChevronDownIcon } from '../../Icons/ChevronDownIcon';
import { Text } from '../../Text/Text';
import { IlinkListEntry } from '../PreFooterLinks';

interface IProps {
  linkList: IlinkListEntry[];
  headingText: string;
}

/**
 * Component that renders a list of links under a heading, with a collapsible
 * behavior on mobile screens.
 *
 * @component
 * @param {IProps} props - The props for the component.
 * @param {IlinkListEntry[]} props.linkList - The list of links to display.
 * @param {string} props.headingText - The text to display as the heading.
 *
 * @returns {JSX.Element | null} The rendered component or null if the link list is empty.
 *
 * @example
 * const linkList = [
 *   { id: '1', name: 'Link 1', url: '/link1', className: 'link-class' },
 *   { id: '2', name: 'Link 2', url: '/link2', className: 'link-class' }
 * ];
 * const headingText = 'Useful Links';
 *
 * <PreFooterLinksEntry linkList={linkList} headingText={headingText} />
 */
export const PreFooterLinksEntry = ({ linkList, headingText }: IProps) => {
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const [isBlockOpened, setIsBlockOpened] = useState<boolean>(false);
  const handleButtonClick = () => {
    setIsBlockOpened(!isBlockOpened);
  };
  if (linkList.length === 0) {
    return null;
  }
  return (
    <div className="flex grow flex-col gap-3">
      <Button
        variant="textNone"
        disabled={!isMobileScreenWidthOrThinner}
        size="none"
        className="flex items-center justify-between text-left"
        onClick={handleButtonClick}
      >
        <Heading as="span" variant="2xs">
          {headingText}
        </Heading>
        <div
          className={`text-abyss transition-all duration-300 sm:hidden ${
            isBlockOpened ? 'rotate-180' : ''
          }`}
        >
          <ChevronDownIcon />
        </div>
      </Button>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mb-0">
        {linkList.map(link => {
          const { id, name, url, className } = link;
          return (
            <InternalLink
              className={`${isBlockOpened ? 'flex' : 'hidden'} ${className} sm:flex`}
              variant="textNone"
              size="none"
              key={id}
              to={url}
            >
              <Text as="span" variant="sm">
                {name}
              </Text>
            </InternalLink>
          );
        })}
      </div>
    </div>
  );
};
